// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  features/dashboard/components/DashboardEditor.tsx
//  Editor de layout de widgets (grid drag/resize + galeria + modelos).
//  Reutilizado pelo Index (modo edição, filtra catálogo pelas permissões do
//  usuário) e pela tela de layout POR PERFIL (admin monta para terceiros —
//  catálogo completo, filtrarPorPermissao=false; o filtro por permissão
//  acontece na renderização do dashboard de cada usuário final).
// ════════════════════════════════════════════════════════════════════════════

import { useState } from "react";
// Subpath /legacy = API v1 (WidthProvider + props planas) mantida pela v2 do
// react-grid-layout. O entry raiz da v2 tem outra API e NÃO exporta WidthProvider.
import GridLayout, { WidthProvider, type Layout, type LayoutItem } from "react-grid-layout/legacy";
import "react-grid-layout/css/styles.css";
import { LayoutTemplate, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { usePermissoes } from "@/contexts/PermissoesContext";
import { CATALOGO_WIDGETS, TEMPLATES_SEGMENTO, widgetPorId, type WidgetDef } from "../catalogo";
import type { WidgetPos } from "../types";

const Grid = WidthProvider(GridLayout);

interface DashboardEditorProps {
  widgets: WidgetPos[];
  onChange: (widgets: WidgetPos[]) => void;
  /** true (default): catálogo/modelos filtrados pelas permissões do usuário logado. */
  filtrarPorPermissao?: boolean;
}

export function DashboardEditor({ widgets, onChange, filtrarPorPermissao = true }: DashboardEditorProps) {
  const { temPermissao } = usePermissoes();
  const [galeriaAberta, setGaleriaAberta] = useState(false);
  const [modelosAberto, setModelosAberto] = useState(false);

  const permitido = (def: WidgetDef) =>
    !filtrarPorPermissao || def.formularioId === null || temPermissao(def.formularioId, "ler");

  const disponiveis = CATALOGO_WIDGETS.filter(
    (def) => !widgets.some((w) => w.id === def.id) && permitido(def),
  );

  const layoutRgl: LayoutItem[] = widgets.map((w) => {
    const def = widgetPorId(w.id);
    return { i: w.id, x: w.x, y: w.y, w: w.w, h: w.h, minW: def?.minW, minH: def?.minH };
  });

  const onLayoutChange = (novo: Layout) => {
    onChange(
      widgets.map((w) => {
        const l = novo.find((n) => n.i === w.id);
        return l ? { ...w, x: l.x, y: l.y, w: l.w, h: l.h } : w;
      }),
    );
  };

  const remover = (id: string) => onChange(widgets.filter((w) => w.id !== id));

  const adicionar = (defId: string) => {
    const def = widgetPorId(defId);
    if (!def) return;
    const y = widgets.reduce((max, w) => Math.max(max, w.y + w.h), 0);
    onChange([...widgets, { id: def.id, x: 0, y, w: def.w, h: def.h }]);
    setGaleriaAberta(false);
  };

  const aplicarTemplate = (templateId: string) => {
    const template = TEMPLATES_SEGMENTO.find((t) => t.id === templateId);
    if (!template) return;
    onChange(
      template.widgets.filter((pos) => {
        const def = widgetPorId(pos.id);
        return !!def && permitido(def);
      }),
    );
    setModelosAberto(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => setGaleriaAberta(true)} disabled={disponiveis.length === 0}>
          <Plus className="h-4 w-4 mr-1" /> Adicionar widget
        </Button>
        <Button variant="outline" size="sm" onClick={() => setModelosAberto(true)}>
          <LayoutTemplate className="h-4 w-4 mr-1" /> Modelos
        </Button>
        <p className="text-xs text-muted-foreground">
          Arraste para mover, use o canto inferior direito para redimensionar e o × para remover.
        </p>
      </div>

      {widgets.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground border border-dashed border-border/60 rounded-lg">
          Nenhum widget. Adicione widgets ou comece de um modelo.
        </div>
      ) : (
        <Grid
          layout={layoutRgl}
          cols={12}
          rowHeight={60}
          margin={[16, 16]}
          containerPadding={[0, 0]}
          isDraggable
          isResizable
          draggableCancel=".widget-no-drag"
          onLayoutChange={onLayoutChange}
          compactType="vertical"
        >
          {widgets.map((w) => {
            const def = widgetPorId(w.id);
            if (!def) return null;
            const Comp = def.componente;
            return (
              <div key={w.id} className="relative rounded-lg ring-2 ring-primary/20">
                <button
                  type="button"
                  aria-label={`Remover ${def.titulo}`}
                  className="widget-no-drag absolute -top-2 -right-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow"
                  onClick={() => remover(w.id)}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
                <Comp
                  modoEdicao
                  config={w.config}
                  onConfigChange={(config) =>
                    onChange(widgets.map((x) => (x.id === w.id ? { ...x, config } : x)))
                  }
                />
              </div>
            );
          })}
        </Grid>
      )}

      <Dialog open={galeriaAberta} onOpenChange={setGaleriaAberta}>
        {/* Conteúdo só montado enquanto aberto: o fechamento desmonta na hora,
            sem depender do animationend — evita overlay/pointer-events presos. */}
        {galeriaAberta && (
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Adicionar widget</DialogTitle>
            <DialogDescription>Escolha um widget para incluir no dashboard.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-auto">
            {disponiveis.map((def) => (
              <button
                key={def.id}
                type="button"
                className="w-full text-left p-3 rounded-lg border border-border/60 hover:bg-muted/50 transition-colors"
                onClick={() => adicionar(def.id)}
              >
                <p className="text-sm font-medium text-foreground">
                  {def.titulo}
                  <span className="ml-2 text-xs text-muted-foreground">{def.categoria}</span>
                </p>
                <p className="text-xs text-muted-foreground">{def.descricao}</p>
              </button>
            ))}
            {disponiveis.length === 0 && (
              <p className="py-4 text-center text-sm text-muted-foreground">
                Todos os widgets disponíveis já estão no dashboard.
              </p>
            )}
          </div>
        </DialogContent>
        )}
      </Dialog>

      <Dialog open={modelosAberto} onOpenChange={setModelosAberto}>
        {modelosAberto && (
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Começar de um modelo</DialogTitle>
            <DialogDescription>
              Substitui o layout atual pelos widgets do modelo escolhido.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-auto">
            {TEMPLATES_SEGMENTO.map((t) => (
              <button
                key={t.id}
                type="button"
                className="w-full text-left p-3 rounded-lg border border-border/60 hover:bg-muted/50 transition-colors"
                onClick={() => aplicarTemplate(t.id)}
              >
                <p className="text-sm font-medium text-foreground">{t.nome}</p>
                <p className="text-xs text-muted-foreground">{t.descricao}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t.widgets.map((w) => widgetPorId(w.id)?.titulo).filter(Boolean).join(" · ")}
                </p>
              </button>
            ))}
          </div>
        </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
